import { z } from 'zod'

// STORY-043: Types pour le système d'agenda et créneaux

/**
 * Statut des créneaux
 */
export const creneauStatutEnum = z.enum(['libre', 'reserve', 'bloque'])
export type CreneauStatut = z.infer<typeof creneauStatutEnum>

export const creneauStatutLabels: Record<CreneauStatut, string> = {
  libre: 'Libre',
  reserve: 'Réservé',
  bloque: 'Bloqué',
}

export const creneauStatutColors: Record<CreneauStatut, 'mint' | 'gold' | 'brown'> = {
  libre: 'mint',
  reserve: 'gold',
  bloque: 'brown',
}

/**
 * Durée des RDV configurables
 */
export const appointmentDurationEnum = z.enum(['30', '60', '120'])
export type AppointmentDuration = z.infer<typeof appointmentDurationEnum>

export const appointmentDurationLabels: Record<AppointmentDuration, string> = {
  '30': '30 minutes',
  '60': '1 heure',
  '120': '2 heures',
}

/**
 * Statut des RDV (structure réelle de la BDD)
 */
export const rendezVousStatutEnum = z.enum([
  'en_attente',   // En attente de confirmation
  'confirme',     // Confirmé
  'annule',       // Annulé
  'termine',      // Terminé
])
export type RendezVousStatut = z.infer<typeof rendezVousStatutEnum>

export const rendezVousStatutLabels: Record<RendezVousStatut, string> = {
  en_attente: 'En attente',
  confirme: 'Confirmé',
  annule: 'Annulé',
  termine: 'Terminé',
}

export const rendezVousStatutColors: Record<RendezVousStatut, 'mint' | 'gold' | 'brown' | 'beige'> = {
  en_attente: 'gold',
  confirme: 'mint',
  annule: 'brown',
  termine: 'beige',
}

/**
 * Statut des RDV (ancienne structure, conservée pour compatibilité)
 */
export const appointmentStatusEnum = z.enum([
  'pending',      // En attente de confirmation
  'confirmed',    // Confirmé
  'cancelled',    // Annulé
  'completed',    // Terminé
  'no_show',      // Client absent
])
export type AppointmentStatus = z.infer<typeof appointmentStatusEnum>

export const appointmentStatusLabels: Record<AppointmentStatus, string> = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  cancelled: 'Annulé',
  completed: 'Terminé',
  no_show: 'Absent',
}

export const appointmentStatusColors: Record<AppointmentStatus, { variant: 'mint' | 'gold' | 'brown' | 'beige' | 'red' | 'default' }> = {
  pending: { variant: 'gold' },
  confirmed: { variant: 'mint' },
  cancelled: { variant: 'default' },
  completed: { variant: 'beige' },
  no_show: { variant: 'red' },
}

/**
 * Type de vue du calendrier
 */
export type CalendarViewType = 'week' | 'month'

/**
 * Jours de la semaine (0 = Dimanche, 1 = Lundi, etc.)
 */
export const weekdayLabels: Record<number, string> = {
  0: 'Dimanche',
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
}

export const weekdayShortLabels: Record<number, string> = {
  0: 'Dim',
  1: 'Lun',
  2: 'Mar',
  3: 'Mer',
  4: 'Jeu',
  5: 'Ven',
  6: 'Sam',
}

/**
 * Créneau de disponibilité (aligné avec la structure réelle de Supabase)
 */
export const creneauSchema = z.object({
  id: z.string().uuid().optional(),
  professionnel_id: z.string().uuid().nullable().optional(),
  start: z.string(), // ISO 8601
  end_time: z.string(), // ISO 8601
  statut: z.enum(['libre', 'reserve', 'bloque']).default('libre'),
})

export type Creneau = z.infer<typeof creneauSchema> & {
  id: string
  created_at: string
  updated_at: string
}

export type CreneauFormData = z.infer<typeof creneauSchema>

// Alias pour compatibilité avec le code existant
export const timeSlotSchema = creneauSchema
export type TimeSlot = Creneau
export type TimeSlotFormData = CreneauFormData

/**
 * Configuration de l'agenda d'un admin
 */
export const agendaConfigSchema = z.object({
  id: z.string().uuid().optional(),
  admin_id: z.string().uuid(),
  default_duration: appointmentDurationEnum.default('60'),
  min_booking_notice_hours: z.number().min(0).max(168).default(24), // Délai minimum pour réserver (en heures)
  max_booking_advance_days: z.number().min(1).max(365).default(30), // Réservation max X jours à l'avance
  buffer_before_minutes: z.number().min(0).max(60).default(0), // Temps de pause avant le RDV
  buffer_after_minutes: z.number().min(0).max(60).default(15), // Temps de pause après le RDV
  timezone: z.string().default('Europe/Paris'),
  is_booking_enabled: z.boolean().default(true),
  booking_page_url: z.string().nullable().optional(),
  // Email de notification
  notification_email: z.string().email().nullable().optional(),
  send_confirmation_email: z.boolean().default(true),
  send_reminder_email: z.boolean().default(true),
  // Personnalisation
  welcome_message: z.string().nullable().optional(),
  confirmation_message: z.string().nullable().optional(),
})

export type AgendaConfig = z.infer<typeof agendaConfigSchema> & {
  id: string
  created_at: string
  updated_at: string
}

export type AgendaConfigFormData = z.infer<typeof agendaConfigSchema>

/**
 * Rendez-vous (aligné avec la structure réelle de Supabase)
 */
export const rendezVousSchema = z.object({
  id: z.string().uuid().optional(),
  client_id: z.string().uuid(),
  professionnel_id: z.string().uuid().nullable().optional(),
  date_heure: z.string(), // ISO 8601
  description: z.string().nullable().optional(),
  statut: z.enum(['en_attente', 'confirme', 'annule', 'termine']).default('en_attente'),
  creneau_id: z.string().uuid().nullable().optional(),
})

export type RendezVous = z.infer<typeof rendezVousSchema> & {
  id: string
  created_at: string
  updated_at: string
  client?: {
    id: string
    email: string
    full_name: string | null
  }
  professionnel?: {
    id: string
    email: string
    full_name: string | null
  }
}

export type RendezVousFormData = z.infer<typeof rendezVousSchema>

// Alias pour compatibilité avec le code existant
export const appointmentSchema = rendezVousSchema
export type Appointment = RendezVous
export type AppointmentFormData = RendezVousFormData

/**
 * CalendarItem - Type unifié pour le CalendarView
 * Peut être un RDV ou une tâche projet
 */
export interface CalendarAppointment {
  id: string
  type: 'appointment'
  start_datetime: string
  status: AppointmentStatus
  client_name: string
  client_email?: string
  subject: string
  description?: string | null
  rdv_id: string
  statut_fr: RendezVousStatut
  participants?: string[]
}

export interface CalendarTask {
  id: string
  type: 'task'
  start_datetime: string
  status: string
  title: string
  project_name: string
  project_id: string
  priority: string
}

export type CalendarItem = CalendarAppointment | CalendarTask

/**
 * Créneau disponible (calculé)
 */
export interface AvailableSlot {
  start: Date
  end: Date
  duration: number
}

/**
 * Jour avec créneaux disponibles
 */
export interface DayAvailability {
  date: Date
  slots: AvailableSlot[]
  isToday: boolean
  isWeekend: boolean
}

/**
 * Filtres pour les RDV (aligné avec la structure réelle)
 */
export interface AppointmentFilters {
  professionnelId?: string
  clientId?: string
  statut?: 'en_attente' | 'confirme' | 'annule' | 'termine'
  dateFrom?: string
  dateTo?: string
}

// Alias pour compatibilité
export interface AgendaFilters extends AppointmentFilters {}

/**
 * Statistiques de l'agenda (aligné avec la structure réelle)
 */
export interface AgendaStats {
  total: number
  en_attente: number
  confirme: number
  annule: number
  termine: number
  today: number
  thisWeek: number
}

/**
 * Données pour la création d'un RDV public (page client)
 */
export interface BookingFormData {
  adminId: string
  slotStart: string
  duration: number
  clientName: string
  clientEmail: string
  clientPhone?: string
  subject: string
  description?: string
}

/**
 * Réponse de réservation
 */
export interface BookingResponse {
  success: boolean
  appointment?: Appointment
  error?: string
  modificationToken?: string
}

/**
 * Rappel de RDV (STORY-045)
 */
export const appointmentReminderSchema = z.object({
  id: z.string().uuid().optional(),
  appointment_id: z.string().uuid(),
  type: z.enum(['24h', '1h']),
  scheduled_at: z.string(),
  sent_at: z.string().nullable().optional(),
  status: z.enum(['pending', 'sent', 'failed']).default('pending'),
  error_message: z.string().nullable().optional(),
})

export type AppointmentReminder = z.infer<typeof appointmentReminderSchema> & {
  id: string
  created_at: string
}

/**
 * Notification admin pour un RDV
 */
export interface AppointmentNotification {
  id: string
  appointment_id: string
  type: 'new_booking' | 'cancellation' | 'reschedule' | 'reminder'
  title: string
  message: string
  read: boolean
  created_at: string
  appointment?: Appointment
}

/**
 * Options d'annulation/report
 */
export interface CancelOptions {
  reason?: string
  notifyClient?: boolean
}

export interface RescheduleOptions {
  newStart: string
  newEnd: string
  notifyClient?: boolean
  reason?: string
}
